// A typed board's own content, as entries: a doc board's pages, a mind map's ideas, a calendar's posts
// and events, and a pipeline's deals. Each is cleaned here, on the server for
// every change and in the browser for what it shows, so a board only ever holds the entries its kind
// does, of a size it can carry.

import { hasLinkedChart, publicCharts } from "./slideChart";
import { BOARD_KINDS, holds, kindOf } from "./boardKinds";
import { cleanIdea, cleanLinks, type NodeData } from "./mindmap";
import { DEFAULT_STAGES, PipelineRefused, cleanDeal, cleanStages, regionCurrency, type DealData, type StagesData } from "./pipeline";
import { cleanPost, type PostData } from "./calendar";
import { BOARD_WRITES, cleanLog, cleanPreviews, cleanQuestion, isPlanStatus, starterOverview, type PlanLog, type PlanPreview, type PlanStatus, type QuestionData } from "./plan";
import { keptPictures } from "./docblocks";
import { cleanPictureRef } from "./imageInfo";
import { MAX_BAND_BYTES, SheetRefused, cleanCells, cleanSheetTab, regionDayFirst, type CellsData, type SheetTabData } from "./sheet/model";
import { SlideRefused, checkSlide, cleanDeck, cleanSlide, type DeckData, type SlideData } from "./slides";
import { RecordRefused, cleanRecordFields } from "./records";
import { AgendaRefused, cleanCalSet, cleanEvent, type CalSetData, type EventData } from "./agenda";
import type { Board, Entry, EntryType, ID } from "./types";

/** An entry's id: letters, digits, _ and -, as a board's. */
export const ENTRY_ID = /^[A-Za-z0-9_-]{4,40}$/;
/** One entry, as JSON: a long page with its blocks fits, a pasted book does not. */
export const MAX_ENTRY_BYTES = 300_000;
/** How deep pages go inside pages. */
export const MAX_DEPTH = 12;

/** A page on a doc board: its title, its emoji, and its body as the page editor keeps it (null until written). */
export interface PageData {
  title: string;
  icon: string | null;
  body: unknown;
  /** on a plan board, a part's status and its history */
  status?: PlanStatus;
  log?: PlanLog[];
  /** a plan's step: the screenshots and mockups its coding agent sent (Previews), the board's to write */
  previews?: PlanPreview[];
}

/** A change refused, with the status the server answers and words a person or an agent reads. */
export class EntryRefused extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const refuse = (status: number, message: string): never => {
  throw new EntryRefused(status, message);
};

/** A page's fields, kept to what a page has: a title on one line, one emoji, and the editor's document. */
function cleanPage(raw: Record<string, unknown>, before: PageData | null, now: number): PageData {
  const title = typeof raw.title === "string" ? raw.title.replace(/[\r\n\t]+/g, " ").slice(0, 200) : (before?.title ?? "");
  const icon = raw.icon === null ? null : typeof raw.icon === "string" && raw.icon.trim() ? raw.icon.trim().slice(0, 16) : (before?.icon ?? null);
  const body = "body" in raw ? cleanBody(raw.body) : (before?.body ?? null);
  // a plan board's part: where it stands, and what happened to it (shared/plan.ts)
  const status = "status" in raw ? (isPlanStatus(raw.status) ? raw.status : undefined) : before?.status;
  // a line new to its history takes the board's time, never the browser's clock, so an agent told what the user did
  // "since it last looked" is told it once, in order. A line it had keeps the board's time, even when the browser sends
  // it back with its own: matched in order, exact first, never against a line before the last one matched, so a line
  // cut from the oldest end never lends its time to a new one. A page put back (an undo) keeps its lines' times.
  const prev = before?.log ?? [];
  let from = 0;
  const same = (p: PlanLog, l: PlanLog) => p.by === l.by && p.kind === l.kind && p.text === l.text;
  // which agents a user's note reached is the board's to say (BOARD_WRITES): sent from a browser or by an agent, a new
  // note can only start its list, empty, and a note it had keeps the board's (Plan Board Fixes, 22 Oct)
  const board = BOARD_WRITES.has(raw);
  const log =
    "log" in raw
      ? cleanLog(raw.log).map((l) => {
          const { got, ...line } = l;
          const kept = (g: PlanLog["got"], at: number): PlanLog => ({ ...line, at, ...(g ? { got: g } : {}) });
          const start = board ? got : got ? [] : undefined;
          if (!before) return kept(start, l.at > 0 ? Math.min(l.at, now) : now);
          let k = prev.findIndex((p, i) => i >= from && p.at === l.at && same(p, l));
          if (k < 0) k = prev.findIndex((p, i) => i >= from && same(p, l));
          if (k < 0) return kept(start, now);
          from = k + 1;
          // what a blocked step waits on stays, sent back by a tab from before the board kept it (review, 22 Oct)
          const was = kept(board ? got : prev[k].got, prev[k].at);
          return !was.on && prev[k].on ? { ...was, on: prev[k].on } : was;
        })
      : before?.log;
  // a step's pictures from its coding agent are the board's to write (Previews, Features 3): a browser or an agent
  // sending a page back, with them or without, never adds, drops or changes one
  const previews = board && "previews" in raw ? cleanPreviews(raw.previews) : cleanPreviews(before?.previews);
  return { title, icon, body, ...(status ? { status } : {}), ...(log?.length ? { log } : {}), ...(previews.length ? { previews } : {}) };
}

/** A page's body: the editor's document, { type: "doc", content: [blocks] }, each block a node with a type; anything else is no body. */
function cleanBody(raw: unknown): unknown {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const d = raw as { type?: unknown; content?: unknown };
  if (d.type !== "doc" || !Array.isArray(d.content)) refuse(400, "A page's body is the page editor's document: { type: \"doc\", content: [blocks] }.");
  if (!(d.content as unknown[]).every((b) => b && typeof b === "object" && typeof (b as { type?: unknown }).type === "string")) refuse(400, "Every block in a page's body has a type.");
  return { type: "doc", content: d.content };
}

/** An idea on a mind map: its words on one line, whether its ideas are folded away, and the side it keeps on the root. */
function cleanNode(raw: Record<string, unknown>, before: NodeData | null): NodeData {
  const text = "text" in raw ? cleanIdea(raw.text) : (before?.text ?? "");
  if (!text) refuse(400, "An idea needs some words.");
  const folded = typeof raw.folded === "boolean" ? raw.folded : (before?.folded ?? false);
  const side = raw.side === "left" || raw.side === "right" ? raw.side : raw.side === null ? null : (before?.side ?? null);
  const color = typeof raw.color === "number" && raw.color >= 1 && raw.color <= 5 ? Math.round(raw.color) : raw.color === null ? null : (before?.color ?? null);
  const links = "links" in raw ? cleanLinks(raw.links) : cleanLinks(before?.links);
  // its picture: null or nothing takes it off, as a person does from the idea (Features 2, 21 Oct)
  const image = "image" in raw ? cleanPictureRef(raw.image) : cleanPictureRef(before?.image);
  return { text, folded, side, color, ...(links.length ? { links } : {}), ...(image ? { image } : {}) };
}

/** A pipeline's own refusals, as an entry's: a 400 with its words. */
const pipeline = <T,>(f: () => T): T => {
  try {
    return f();
  } catch (e) {
    if (e instanceof PipelineRefused) refuse(400, e.message);
    throw e;
  }
};

const CLEAN: Record<EntryType, (raw: Record<string, unknown>, before: Record<string, unknown> | null, now: number) => Record<string, unknown>> = {
  page: (raw, before, now) => cleanPage(raw, before as PageData | null, now) as unknown as Record<string, unknown>,
  node: (raw, before) => cleanNode(raw, before as NodeData | null) as unknown as Record<string, unknown>,
  // a client pipeline's deals, and its stages with its currency (shared/pipeline.ts)
  deal: (raw, before) => pipeline(() => cleanDeal(raw, before as DealData | null)) as unknown as Record<string, unknown>,
  stages: (raw, before) => pipeline(() => cleanStages(raw, before as StagesData | null)) as unknown as Record<string, unknown>,
  // a content calendar's post (shared/calendar.ts)
  post: (raw, before) => cleanPost(raw, before as PostData | null, refuse) as unknown as Record<string, unknown>,
  // a plan board's question for the user (shared/plan.ts)
  question: (raw, before, now) => cleanQuestion(raw, before as QuestionData | null, now, refuse) as unknown as Record<string, unknown>,
  // a sheet board's tab, and a band of its cells, a patch laid over what the band has (shared/sheet/model.ts)
  sheet: (raw, before) => sheet(() => cleanSheetTab(raw, before as SheetTabData | null)),
  cells: (raw, before) => sheet(() => cleanCells(raw, before as CellsData | null)),
  // a slides board's slide, and its deck's style (shared/slides.ts)
  // a slide's boxes within their limits as written (S5): one past them is refused, saying which
  slide: (raw, before) => slides(() => checkSlide(cleanSlide(raw, before as SlideData | null))),
  deck: (raw, before) => slides(() => cleanDeck(raw, before as DeckData | null)),
  // a Calendar board's event, and its calendar settings (shared/agenda.ts)
  event: (raw, before) => agenda(() => cleanEvent(raw, before as EventData | null)),
  calset: (raw, before) => agenda(() => cleanCalSet(raw, before as CalSetData | null)),
  // a Custom board's record: the fields its app keeps (shared/records.ts)
  record: (raw) => {
    try {
      return cleanRecordFields(raw);
    } catch (e) {
      if (e instanceof RecordRefused) refuse(e.message.includes("characters") ? 413 : 400, e.message);
      throw e;
    }
  },
};

/** A Calendar board's own refusals, as an entry's: a 400 with its words. */
function agenda(clean: () => object): Record<string, unknown> {
  try {
    return { ...clean() } as Record<string, unknown>;
  } catch (e) {
    if (e instanceof AgendaRefused) refuse(400, e.message);
    throw e;
  }
}

/** A sheet's own refusals, as an entry's: a 400 with its words (a band too full is a 413). */
function sheet(clean: () => object): Record<string, unknown> {
  try {
    return { ...clean() } as Record<string, unknown>;
  } catch (e) {
    if (e instanceof SheetRefused) refuse(/too much text/.test(e.message) ? 413 : 400, e.message);
    throw e;
  }
}

/** A slide's own refusals, as an entry's: a 400 with its words. */
function slides(clean: () => object): Record<string, unknown> {
  try {
    return { ...clean() } as Record<string, unknown>;
  } catch (e) {
    if (e instanceof SlideRefused) refuse(400, e.message);
    throw e;
  }
}

/**
 * An entry as it may be kept on its board: its type one the board's kind holds, its fields cleaned, its
 * size within MAX_ENTRY_BYTES. The board it is on, when the entry exists, never changes, nor its type.
 * Whether its parent is there, on the same board, and not inside it, is the server's to check
 * (worker/workspace.ts), which has every entry.
 */
export function cleanEntry(raw: unknown, ctx: { board: Board | null; existing: Entry | null; now: number; by: string | null }): Entry {
  if (!raw || typeof raw !== "object") refuse(400, "An entry must be an object.");
  const r = raw as Record<string, unknown>;
  const id = typeof r.id === "string" && ENTRY_ID.test(r.id) ? r.id : refuse(400, "An entry needs an id.");
  const was = ctx.existing;
  const board = ctx.board ?? refuse(404, "That board is not there any more.");
  if (was && was.boardId !== board.id) refuse(400, "An entry stays on the board it was made on.");
  const t = was ? was.t : r.t;
  if (typeof t !== "string" || !Object.hasOwn(CLEAN, t)) refuse(400, `There is no kind of entry called "${String(t)}".`);
  const kind = kindOf(board);
  if (!holds(kind, t)) refuse(400, `A ${BOARD_KINDS[kind].name.toLowerCase()} board does not hold a ${t}.`);
  const parent = r.parent === null || r.parent === undefined ? null : typeof r.parent === "string" && ENTRY_ID.test(r.parent) ? r.parent : refuse(400, "An entry's parent is another entry's id, or null.");
  if (parent === id) refuse(400, "An entry cannot sit inside itself.");
  const order = typeof r.order === "number" && Number.isFinite(r.order) ? Math.max(-1e9, Math.min(1e9, r.order)) : (was?.order ?? 0);
  const data = CLEAN[t as EntryType](r.data && typeof r.data === "object" ? (r.data as Record<string, unknown>) : {}, was?.data ?? null, ctx.now);
  // the board's own mark on a page (a note reaching an agent) is no edit: the page keeps who changed it last, and when
  const own = !!was && t === "page" && BOARD_WRITES.has(r.data as object);
  const entry: Entry = { id, boardId: board.id, t: t as EntryType, parent, order, data, createdAt: was?.createdAt ?? ctx.now, updatedAt: own ? was.updatedAt : ctx.now, by: own ? was.by : ctx.by };
  const size = JSON.stringify(entry).length;
  // a sheet's band of 50 rows has its own room, which its cleaner keeps to (a big tab is many bands)
  const max = t === "cells" ? MAX_BAND_BYTES + 2_000 : MAX_ENTRY_BYTES;
  if (size > max) refuse(413, `That ${t} is too large to save (${Math.round(size / 1000)}KB, the limit is ${max / 1000}KB).`);
  return entry;
}

/**
 * An entry as a shared board's visitors get it, when they open it and in every change after: never who changed it,
 * and a page's pictures
 * only once kept (a coworker gets the page as the owner has it, so a picture still uploading is never lost to
 * their edit).
 */
export function publicEntry(e: Entry, coworker = false, dayFirst?: boolean): Omit<Entry, "by"> {
  // a sheet's tab says how its dates read, as its owner's region writes them, so a visitor elsewhere reads the same
  // dates (a sheet made before 28 Oct may not say yet)
  if (e.t === "sheet" && typeof e.data.dmy !== "boolean" && typeof dayFirst === "boolean") e = { ...e, data: { ...e.data, dmy: dayFirst } };
  // a plan step's previews are the owner's alone, coworkers' too: a screenshot can hold private data (Features 3)
  const { by: _by, ...shown } = e.t === "page" && "previews" in e.data ? { ...e, data: (({ previews: _p, ...d }) => d)(e.data) } : e;
  // a page's blocks never say who wrote them, or when, to a visitor (agent colours are the owner's, 20 Oct); a
  // coworker it is shared with sees who wrote each, as the owner does, and keeps it when they edit (sharing)
  const rest = e.t === "page" && e.data.body && !coworker ? { ...shown, data: { ...shown.data, body: keptPictures(withoutAuthors(e.data.body)) } } : shown;
  // a plan's step says what happened to it, never who did it; the owner's notes to their agent, and the agent's
  // messages to them, stay theirs
  if (e.t === "page" && Array.isArray(e.data.log))
    return {
      ...rest,
      data: {
        ...rest.data,
        log: cleanLog(e.data.log)
          .filter((l) => !(l.by === null && l.kind === "note") && l.kind !== "say")
          .map((l) => ({ ...l, by: null })),
      },
    };
  // a slide's speaker notes are the presenter's: a visitor sees the deck, never what its speaker means to say; a chart
  // linked to one of the owner's sheets shows them its numbers, never which sheet they came from (SD)
  if (e.t === "slide" && !coworker && ("notes" in e.data || hasLinkedChart(e.data.boxes))) {
    const { notes: _n, ...d } = rest.data;
    return { ...rest, data: hasLinkedChart(d.boxes) ? { ...d, boxes: publicCharts(d.boxes as unknown[]) } : d };
  }
  // a sheet cell's note is its owner's (Fixes 9 H2a): a visitor on a shared link sees the cells and their looks, never
  // the notes left on them, as a slide's speaker notes; a coworker it is shared with sees them, as the owner does
  if (e.t === "cells" && !coworker) return { ...rest, data: withoutNotes(rest.data) };
  return rest;
}

/** A band of cells (or a patch to one) with every cell's note taken out of its look; a look that was only a note goes. */
export function withoutNotes(data: Record<string, unknown>): Record<string, unknown> {
  const strip = (m: unknown, keepEmpty: boolean): Record<string, unknown> | undefined => {
    if (!m || typeof m !== "object") return undefined;
    const out: Record<string, unknown> = {};
    for (const [a, f] of Object.entries(m as Record<string, unknown>)) {
      if (!f || typeof f !== "object" || !("nt" in f)) {
        out[a] = f;
        continue;
      }
      const { nt: _nt, ...look } = f as Record<string, unknown>;
      // in a patch, a look that was only a note says the cell has no look (null), as it always had for a visitor
      if (Object.keys(look).length) out[a] = look;
      else if (keepEmpty) out[a] = null;
    }
    return out;
  };
  const next = { ...data };
  if (data.fmt) next.fmt = strip(data.fmt, false);
  if (data.fset) next.fset = strip(data.fset, true);
  return next;
}

/** A page body with each block's writer, time and edit count taken out. */
function withoutAuthors(body: unknown): unknown {
  const strip = (n: unknown): unknown => {
    if (!n || typeof n !== "object") return n;
    const node = n as { attrs?: Record<string, unknown>; content?: unknown[] };
    let out: Record<string, unknown> = node as Record<string, unknown>;
    if (node.attrs && ("by" in node.attrs || "at" in node.attrs || "len" in node.attrs || "chg" in node.attrs)) {
      const { by: _b, at: _a, len: _l, chg: _c, ...attrs } = node.attrs;
      out = { ...out, attrs };
    }
    return Array.isArray(node.content) ? { ...out, content: node.content.map(strip) } : out;
  };
  return strip(body);
}

/**
 * Whether a shared board's visitors get an entry at all: a plan's questions hold the owner's answers, so never; nor
 * one of a type no board holds any more, which nothing shows (the retired planner's tasks, calendars and day notes,
 * kept on a board an account had: Features 6).
 */
export function isPublicEntry(e: Pick<Entry, "t">): boolean {
  return e.t !== "question" && Object.hasOwn(CLEAN, e.t);
}

/** A page's title as people read it: an untitled page says so. */
export function pageTitle(e: Pick<Entry, "data"> | null | undefined): string {
  const t = typeof e?.data?.title === "string" ? e.data.title.trim() : "";
  return t || "Untitled";
}

/** The entries directly inside one (or at the top, for null), in their order. */
export function childrenOf<E extends Pick<Entry, "id" | "parent" | "order" | "createdAt">>(all: E[], parent: ID | null): E[] {
  return all.filter((e) => (e.parent ?? null) === parent).sort((a, b) => a.order - b.order || a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1));
}

/** Every entry inside one, at any depth, parents before the entries inside them. */
export function descendants<E extends Pick<Entry, "id" | "parent">>(all: E[], id: ID): E[] {
  const out: E[] = [];
  const seen = new Set<ID>([id]);
  let level = [id];
  while (level.length) {
    const next: ID[] = [];
    for (const e of all)
      if (e.parent && level.includes(e.parent) && !seen.has(e.id)) {
        seen.add(e.id);
        out.push(e);
        next.push(e.id);
      }
    level = next;
  }
  return out;
}

/** Whether putting an entry inside parent would put it inside itself, or deeper than MAX_DEPTH. */
export function badParent<E extends Pick<Entry, "id" | "parent">>(all: E[], id: ID, parent: ID | null, noun = "page"): string | null {
  if (!parent) return null;
  if (parent === id || descendants(all, id).some((e) => e.id === parent)) return `A${noun === "idea" ? "n" : ""} ${noun} cannot go inside itself or a${noun === "idea" ? "n" : ""} ${noun} inside it.`;
  const byId = new Map(all.map((e) => [e.id, e]));
  let depth = 1;
  // how deep the parent sits, then how deep the entry's own pages go under it
  for (let p = byId.get(parent); p?.parent && depth <= MAX_DEPTH; p = byId.get(p.parent)) depth++;
  const below = (from: ID): number => Math.max(0, ...all.filter((e) => e.parent === from).map((e) => 1 + below(e.id)));
  if (depth + 1 + below(id) > MAX_DEPTH + 1) return `${noun === "idea" ? "Ideas" : "Pages"} go up to ${MAX_DEPTH} deep.`;
  return null;
}

export interface TreeRow<E> {
  entry: E;
  depth: number;
  /** how many entries sit directly inside it */
  inside: number;
}

/**
 * A board's entries as a tree read top to bottom: each followed by the entries inside it, one level
 * deeper. An entry whose parent is missing sits at the top. open says whether to go inside one.
 */
export function treeRows<E extends Pick<Entry, "id" | "parent" | "order" | "createdAt">>(all: E[], open: (id: ID) => boolean = () => true): TreeRow<E>[] {
  const ids = new Set(all.map((e) => e.id));
  const top = all.map((e) => (e.parent && ids.has(e.parent) ? e : { ...e, parent: null }));
  const rows: TreeRow<E>[] = [];
  const walk = (parent: ID | null, depth: number) => {
    for (const e of childrenOf(top, parent)) {
      const inside = top.filter((x) => x.parent === e.id).length;
      rows.push({ entry: all.find((x) => x.id === e.id)!, depth, inside });
      if (inside && open(e.id) && depth < MAX_DEPTH) walk(e.id, depth + 1);
    }
  };
  walk(null, 0);
  return rows;
}

/** The order that puts an entry at index among siblings (already in their order, without it). */
export function orderAt<E extends Pick<Entry, "order">>(siblings: E[], index: number): number {
  const before = siblings[index - 1]?.order;
  const after = siblings[index]?.order;
  if (before === undefined && after === undefined) return 0;
  if (before === undefined) return after! - 1;
  if (after === undefined) return before + 1;
  return (before + after) / 2;
}

/**
 * What a new board of a kind starts with: a doc board opens on one page, "Start here"; a mind map on its root idea, the
 * board's name; a pipeline on its four stages; a deck on a Title slide, the board's name; a sheet on its first tab.
 * dayFirst: how the account's region writes dates, for a sheet (else its time zone, tz, says).
 */
export function starterEntries(board: Pick<Board, "id" | "kind"> & { name?: string }, now: number, newId: () => ID, tz: string | null = null, _locale: string | null = null, dayFirst?: boolean): Entry[] {
  // a sheet opens on its first tab, "Sheet 1", with dates read the way the account's region writes them, said on the
  // tab so a later change of time zone (a trip abroad) never reads its dates again another way
  if (kindOf(board) === "sheet") return [{ id: newId(), boardId: board.id, t: "sheet", parent: null, order: 0, data: { name: "Sheet 1", dmy: dayFirst ?? regionDayFirst(tz ?? null) }, createdAt: now, updatedAt: now, by: null }];
  if (kindOf(board) === "mindmap") return [{ id: newId(), boardId: board.id, t: "node", parent: null, order: 0, data: { text: cleanIdea(board.name) || "Main idea", folded: false, side: null }, createdAt: now, updatedAt: now, by: null }];
  // a pipeline in the account's region's currency (Fixes 9): pounds in the UK, euros in the euro area, else dollars
  if (kindOf(board) === "pipeline") return [{ id: newId(), boardId: board.id, t: "stages", parent: null, order: 0, data: { stages: DEFAULT_STAGES, currency: regionCurrency(tz), tz }, createdAt: now, updatedAt: now, by: null }];
  // a plan opens on its Overview, with the headings its agent fills in
  if (kindOf(board) === "plan") return [{ id: newId(), boardId: board.id, t: "page", parent: null, order: 0, data: { title: "Overview", icon: "🧭", body: starterOverview() }, createdAt: now, updatedAt: now, by: null }];
  // a deck opens on a Title slide with the board's name, in the board's own colours
  if (kindOf(board) === "slides")
    return [
      { id: newId(), boardId: board.id, t: "deck", parent: null, order: -1, data: { style: "board", accent: null }, createdAt: now, updatedAt: now, by: null },
      { id: newId(), boardId: board.id, t: "slide", parent: null, order: 0, data: { layout: "title", title: (board.name ?? "").trim().slice(0, 240), body: null }, createdAt: now, updatedAt: now, by: null },
    ];
  if (kindOf(board) !== "doc") return [];
  return [{ id: newId(), boardId: board.id, t: "page", parent: null, order: 0, data: { title: "Start here", icon: null, body: null }, createdAt: now, updatedAt: now, by: null }];
}
